import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { ApiError, api } from '../api/client.js'
import type { Absence, CoverRule } from '../api/types.js'
import { Icon } from '../components/icons.js'
import { useToast } from '../components/toast.js'
import { Notice } from '../components/ui.js'
import { formatDate, todayIso } from '../lib/format.js'

const KIND_LABEL: Record<Absence['kind'], string> = { krank: 'krank', urlaub: 'Urlaub' }

/**
 * Krank oder Urlaub mit Zeitraum. Ersetzt den Eintrag im Feld "Nutzer/Bewohner"
 * aus dem Hausmanager: Die Aufgabenliste zeigt den Hinweis an jeder Aufgabe
 * der Person in dieser Zeit von selbst. "Vertretung regeln" öffnet die
 * Verteilung für genau diesen Zeitraum, auch Monate im Voraus; was dort
 * gewählt wird, steht darunter.
 */
export function AbsencesCard({ staffId }: { staffId: string }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const today = todayIso()
  const [error, setError] = useState<string | null>(null)
  const { data } = useQuery({ queryKey: ['absences', staffId], queryFn: () => api<Absence[]>(`/api/staff/${staffId}/absences`) })
  const rules = useQuery({ queryKey: ['cover-rules', staffId], queryFn: () => api<CoverRule[]>(`/api/staff/${staffId}/cover-rules`) })
  const endRule = useMutation({
    mutationFn: (id: string) => api(`/api/cover-rules/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cover-rules', staffId] }),
  })
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['absences', staffId] })
    await queryClient.invalidateQueries({ queryKey: ['staff'] })
    await queryClient.invalidateQueries({ queryKey: ['occurrences'] })
  }
  const add = useMutation({
    mutationFn: (body: Record<string, unknown>) => api(`/api/staff/${staffId}/absences`, { method: 'POST', body }),
    onSuccess: async () => {
      setError(null)
      toast('Eingetragen. Über „Vertretung regeln“ die Aufgaben in dieser Zeit verteilen.')
      await refresh()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })
  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/absences/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
  })
  const current = (data ?? []).filter((a) => a.until >= today)
  const past = (data ?? []).filter((a) => a.until < today).slice(0, 5)

  return (
    <section className="card">
      <div className="card-head">
        <h2>
          <Icon name="clock" size={16} /> Abwesenheit
        </h2>
      </div>
      <div className="card-body">
        {error && <Notice kind="error">{error}</Notice>}
        <form
          className="absence-form"
          onSubmit={(event: FormEvent<HTMLFormElement>) => {
            event.preventDefault()
            const form = new FormData(event.currentTarget)
            add.mutate({ kind: form.get('kind'), from: form.get('from'), until: form.get('until'), note: form.get('note') || undefined })
            event.currentTarget.reset()
          }}
        >
          <select name="kind" defaultValue="krank" aria-label="Art">
            <option value="krank">krank</option>
            <option value="urlaub">Urlaub</option>
          </select>
          <input type="date" name="from" required defaultValue={today} aria-label="Von" />
          <input type="date" name="until" required defaultValue={today} aria-label="Bis" />
          <input name="note" placeholder="Notiz, z. B. Vertretung offen" maxLength={300} aria-label="Notiz" />
          <button type="submit" className="btn small" disabled={add.isPending}>
            Eintragen
          </button>
        </form>
        {current.length === 0 && past.length === 0 ? (
          <p className="muted small" style={{ marginBottom: 0 }}>Keine Abwesenheit eingetragen.</p>
        ) : (
          <ul className="absence-list">
            {[...current, ...past].map((a) => (
              <li key={a.id} className={a.until < today ? 'past' : ''}>
                <span className={`badge ${a.until < today ? '' : 'warn'}`}>{KIND_LABEL[a.kind]}</span>
                <span>
                  {formatDate(a.from)} bis {formatDate(a.until)}
                  {a.note && <span className="muted">, {a.note}</span>}
                </span>
                {a.until >= today && (
                  <Link className="btn secondary small" to={`/mitarbeiter/${staffId}/vertretung?von=${a.from < today ? today : a.from}&bis=${a.until}`}>
                    Vertretung regeln
                  </Link>
                )}
                <button type="button" className="btn ghost small" onClick={() => remove.mutate(a.id)}>
                  Entfernen
                </button>
              </li>
            ))}
          </ul>
        )}
        {(rules.data ?? []).length > 0 && (
          <>
            <h3 className="small strong" style={{ margin: '14px 0 6px' }}>Vertretung festgelegt</h3>
            <ul className="absence-list">
              {rules.data!.map((r) => (
                <li key={r.id}>
                  <span>
                    {formatDate(r.from)} bis {formatDate(r.until)}: <span className="strong">{r.substituteName}</span>
                    <span className="muted">, {r.siteName ?? 'alle übrigen Liegenschaften'}</span>
                  </span>
                  <button type="button" className="btn ghost small" onClick={() => endRule.mutate(r.id)}>
                    Aufheben
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </section>
  )
}
